import React, { useEffect, useState } from 'react'
import { api } from '../../api.js'
import { useConfig } from '../../config.jsx'
import { Card, Field, Alert, usePager, Pager } from '../../lib.jsx'

const TABS = [
  { key: 'general', label: 'Ogólne' },
  { key: 'integrations', label: 'Integracje' },
  { key: 'templates', label: 'Szablony e-mail' },
  { key: 'translations', label: 'Tłumaczenia' },
  { key: 'notifications', label: 'Powiadomienia (log)' },
]

// ---------------------------------------------------------------------------
// Ogólne — SystemSetting generyczny (klucz/wartość JSON/opis)
// ---------------------------------------------------------------------------
function SettingRow({ setting, onSaved }) {
  const [value, setValue] = useState(JSON.stringify(setting.value ?? {}, null, 2))
  const [description, setDescription] = useState(setting.description || '')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setErr(''); setBusy(true)
    try {
      let parsed
      try { parsed = JSON.parse(value) } catch { throw new Error('Wartość musi być poprawnym JSON-em') }
      await api.putAdminSetting(setting.key, { value: parsed, description })
      onSaved()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  return (
    <div className="unit-row">
      <Alert kind="err">{err}</Alert>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <Field label="Klucz"><input value={setting.key} readOnly disabled /></Field>
        <Field label="Opis"><input value={description} onChange={e => setDescription(e.target.value)} /></Field>
      </div>
      <Field label="Wartość (JSON)"><textarea rows={4} className="mono" value={value} onChange={e => setValue(e.target.value)} /></Field>
      <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>{busy ? 'Zapisywanie…' : 'Zapisz'}</button>
    </div>
  )
}

function NewSettingForm({ onSaved }) {
  const [key, setKey] = useState('')
  const [value, setValue] = useState('{}')
  const [description, setDescription] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    setErr(''); setBusy(true)
    try {
      let parsed
      try { parsed = JSON.parse(value) } catch { throw new Error('Wartość musi być poprawnym JSON-em') }
      await api.putAdminSetting(key.trim(), { value: parsed, description })
      setKey(''); setValue('{}'); setDescription('')
      onSaved()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit}>
      <Alert kind="err">{err}</Alert>
      <div className="row">
        <Field label="Nowy klucz"><input value={key} onChange={e => setKey(e.target.value)} required /></Field>
        <Field label="Opis"><input value={description} onChange={e => setDescription(e.target.value)} /></Field>
      </div>
      <Field label="Wartość (JSON)"><textarea rows={3} className="mono" value={value} onChange={e => setValue(e.target.value)} /></Field>
      <button className="btn btn-primary" disabled={busy || !key.trim()}>{busy ? 'Dodawanie…' : 'Dodaj nowe ustawienie'}</button>
    </form>
  )
}

function GeneralTab() {
  const [settings, setSettings] = useState([])
  const [err, setErr] = useState('')
  const load = () => api.adminSettings().then(setSettings).catch(e => setErr(e.message))
  useEffect(() => { load() }, [])

  return (
    <div className="grid grid-2" style={{ alignItems: 'start' }}>
      <Card title="Dodaj nowe ustawienie"><NewSettingForm onSaved={load} /></Card>
      <Card title="Ustawienia systemowe" desc={`${settings.length} łącznie`}>
        <Alert kind="err">{err}</Alert>
        {settings.length === 0 ? <div className="empty">Brak zapisanych ustawień.</div> :
          settings.map(s => <SettingRow key={s.key} setting={s} onSaved={load} />)}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Integracje — SMTP + Stripe
// ---------------------------------------------------------------------------
const SMTP_DEFAULTS = { backend: 'console', enabled: false, host: '', port: 587, user: '', from_email: '', from_name: '', use_tls: true, use_ssl: false }

function SmtpCard() {
  const [smtp, setSmtp] = useState(SMTP_DEFAULTS)
  const [password, setPassword] = useState('')
  const [passwordSet, setPasswordSet] = useState(false)
  const [testAddr, setTestAddr] = useState('')
  const [testResult, setTestResult] = useState(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [testBusy, setTestBusy] = useState(false)

  const load = () => {
    api.adminSettings().then(rows => {
      const row = rows.find(r => r.key === 'smtp')
      if (row) setSmtp({ ...SMTP_DEFAULTS, ...row.value })
    }).catch(e => setErr(e.message))
    api.adminSecrets().then(rows => setPasswordSet(!!rows.find(r => r.key === 'smtp_password')?.is_set)).catch(() => {})
  }
  useEffect(load, [])

  const set = (k, v) => setSmtp(s => ({ ...s, [k]: v }))

  const save = async () => {
    setErr(''); setBusy(true)
    try {
      await api.putAdminSetting('smtp', { value: smtp, description: 'Konfiguracja serwera SMTP' })
      if (password) { await api.putAdminSecret('smtp_password', { value: password, description: 'Hasło SMTP' }); setPassword(''); setPasswordSet(true) }
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const sendTest = async () => {
    setTestBusy(true); setTestResult(null)
    try { const res = await api.testEmailTemplate(testAddr); setTestResult(res.sent ? 'ok' : 'fail') }
    catch { setTestResult('fail') } finally { setTestBusy(false) }
  }

  return (
    <Card title="SMTP" desc="Serwer poczty wychodzącej — powiadomienia, weryfikacja e-mail, zaproszenia.">
      <Alert kind="err">{err}</Alert>
      <div className="row">
        <Field label="Backend">
          <select value={smtp.backend} onChange={e => set('backend', e.target.value)}>
            <option value="console">console (tylko log, bez realnej wysyłki)</option>
            <option value="smtp">smtp</option>
          </select>
        </Field>
        <Field label="Włączony"><label className="check"><input type="checkbox" checked={!!smtp.enabled} onChange={e => set('enabled', e.target.checked)} /> aktywny</label></Field>
      </div>
      <div className="row">
        <Field label="Host"><input value={smtp.host} onChange={e => set('host', e.target.value)} /></Field>
        <Field label="Port"><input type="number" value={smtp.port} onChange={e => set('port', Number(e.target.value))} /></Field>
      </div>
      <div className="row">
        <Field label="Użytkownik"><input value={smtp.user} onChange={e => set('user', e.target.value)} /></Field>
        <Field label="Hasło" hint={passwordSet ? 'już ustawione — wpisz, aby nadpisać' : 'nieustawione'}>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={passwordSet ? '••••••••' : ''} />
        </Field>
      </div>
      <div className="row">
        <Field label="Adres nadawcy"><input value={smtp.from_email} onChange={e => set('from_email', e.target.value)} /></Field>
        <Field label="Nazwa nadawcy"><input value={smtp.from_name} onChange={e => set('from_name', e.target.value)} /></Field>
      </div>
      <div className="row">
        <label className="check"><input type="checkbox" checked={!!smtp.use_tls} onChange={e => set('use_tls', e.target.checked)} /> STARTTLS</label>
        <label className="check"><input type="checkbox" checked={!!smtp.use_ssl} onChange={e => set('use_ssl', e.target.checked)} /> SSL</label>
      </div>
      <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Zapisywanie…' : 'Zapisz SMTP'}</button>

      <div className="divider" />
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <Field label="Wyślij testowy e-mail na adres"><input type="email" value={testAddr} onChange={e => setTestAddr(e.target.value)} /></Field>
        <button className="btn" disabled={testBusy || !testAddr} onClick={sendTest} style={{ marginBottom: 16 }}>{testBusy ? 'Wysyłanie…' : 'Wyślij testowy e-mail'}</button>
      </div>
      {testResult === 'ok' && <Alert kind="ok">Wiadomość wysłana (albo zalogowana w trybie console).</Alert>}
      {testResult === 'fail' && <Alert kind="err">Wysyłka nie powiodła się — sprawdź konfigurację SMTP.</Alert>}
    </Card>
  )
}

function StripeCard() {
  const KEYS = [
    { key: 'stripe_secret_key', label: 'Secret key' },
    { key: 'stripe_webhook_secret', label: 'Webhook secret' },
    { key: 'stripe_publishable_key', label: 'Publishable key' },
  ]
  const [values, setValues] = useState({})
  const [isSet, setIsSet] = useState({})
  const [err, setErr] = useState('')
  const [busyKey, setBusyKey] = useState(null)

  const load = () => api.adminSecrets().then(rows => {
    const map = {}
    for (const r of rows) map[r.key] = r.is_set
    setIsSet(map)
  }).catch(e => setErr(e.message))
  useEffect(load, [])

  const save = async (key) => {
    const v = values[key]
    if (!v) return
    setBusyKey(key); setErr('')
    try { await api.putAdminSecret(key, { value: v }); setValues(s => ({ ...s, [key]: '' })); load() }
    catch (e) { setErr(e.message) } finally { setBusyKey(null) }
  }

  return (
    <Card title="Stripe" desc="Sekrety zapisywane wyłącznie zaszyfrowane — nigdy nie są odczytywane z powrotem do przeglądarki.">
      <Alert kind="err">{err}</Alert>
      {KEYS.map(({ key, label }) => (
        <Field key={key} label={label} hint={isSet[key] ? 'już ustawione — wpisz, aby nadpisać' : 'nieustawione'}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="password" value={values[key] || ''} onChange={e => setValues(s => ({ ...s, [key]: e.target.value }))}
              placeholder={isSet[key] ? '••••••••' : ''} />
            <button className="btn btn-sm" disabled={busyKey === key || !values[key]} onClick={() => save(key)}>
              {busyKey === key ? 'Zapisywanie…' : 'Zapisz'}
            </button>
          </div>
        </Field>
      ))}
    </Card>
  )
}

function IntegrationsTab() {
  return <div className="grid grid-2" style={{ alignItems: 'start' }}><SmtpCard /><StripeCard /></div>
}

// ---------------------------------------------------------------------------
// Szablony e-mail
// ---------------------------------------------------------------------------
function TemplatesTab() {
  const [items, setItems] = useState([])
  const [sel, setSel] = useState(null) // { key, language }
  const [detail, setDetail] = useState(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => api.emailTemplates().then(setItems).catch(e => setErr(e.message))
  useEffect(() => { load() }, [])

  const select = (key, language) => {
    setSel({ key, language }); setErr('')
    api.emailTemplate(key, language).then(d => {
      setDetail(d); setSubject(d.subject); setBody(d.body); setEnabled(true)
    }).catch(e => setErr(e.message))
  }

  const save = async () => {
    if (!sel) return
    setBusy(true); setErr('')
    try {
      const d = await api.putEmailTemplate(sel.key, sel.language, { subject, body, enabled })
      setDetail(d); load()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const restore = async () => {
    if (!sel) return
    setBusy(true); setErr('')
    try {
      await api.deleteEmailTemplate(sel.key, sel.language)
      select(sel.key, sel.language); load()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  return (
    <div className="grid grid-3" style={{ alignItems: 'start' }}>
      <Card title="Klucze szablonów">
        <Alert kind="err">{err}</Alert>
        {items.map(it => (
          <div key={it.key} className="unit-row">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{it.key}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {it.languages.map(l => (
                <button key={l.language} type="button"
                  className={`btn btn-sm ${sel?.key === it.key && sel?.language === l.language ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => select(it.key, l.language)}>
                  {l.language}{l.overridden ? ' · nadpisany' : ''}
                </button>
              ))}
            </div>
          </div>
        ))}
      </Card>
      <div style={{ gridColumn: 'span 2' }}>
        <Card title="Edytor" desc={sel ? `${sel.key} · ${sel.language}` : 'Wybierz szablon z listy'}>
          {!detail ? <div className="empty">Wybierz klucz i język z lewej kolumny.</div> : (
            <>
              <Field label="Temat"><input value={subject} onChange={e => setSubject(e.target.value)} /></Field>
              <Field label="Treść"><textarea rows={10} value={body} onChange={e => setBody(e.target.value)} /></Field>
              {detail.variables.length > 0 && (
                <div className="muted small" style={{ marginBottom: 12 }}>Zmienne: {detail.variables.map(v => <span key={v} className="pill" style={{ marginRight: 6 }}>{`{{ ${v} }}`}</span>)}</div>
              )}
              <label className="check" style={{ marginBottom: 16 }}>
                <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} /> Nadpisanie aktywne
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Zapisywanie…' : 'Zapisz'}</button>
                <button className="btn btn-ghost" disabled={busy || !detail.is_override} onClick={restore}>Przywróć domyślny</button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tłumaczenia
// ---------------------------------------------------------------------------
function TranslationsTab() {
  const cfg = useConfig()
  const [keys, setKeys] = useState([])
  const [lang, setLang] = useState('pl')
  const [byKey, setByKey] = useState({}) // key_id -> Translation
  const [edits, setEdits] = useState({}) // key_id -> string
  const [err, setErr] = useState('')
  const [busyKey, setBusyKey] = useState(null)

  const load = () => {
    api.i18nKeys().then(setKeys).catch(e => setErr(e.message))
    api.i18nTranslations({ language: lang }).then(rows => {
      const map = {}
      for (const t of rows) map[t.key_id] = t
      setByKey(map); setEdits({})
    }).catch(e => setErr(e.message))
  }
  useEffect(load, [lang])

  const save = async (keyId) => {
    const value = edits[keyId] ?? byKey[keyId]?.value ?? ''
    setBusyKey(keyId); setErr('')
    try {
      const existing = byKey[keyId]
      if (existing) await api.patchTranslation(existing.id, { value })
      else await api.createTranslation({ key_id: keyId, language: lang, value, is_reviewed: true })
      load()
    } catch (e) { setErr(e.message) } finally { setBusyKey(null) }
  }

  const suggest = async (keyId) => {
    let existing = byKey[keyId]
    setBusyKey(keyId); setErr('')
    try {
      if (!existing) existing = await api.createTranslation({ key_id: keyId, language: lang, value: '', is_reviewed: false })
      await api.aiSuggestTranslation(existing.id)
      load()
    } catch (e) { setErr(e.message) } finally { setBusyKey(null) }
  }

  return (
    <Card title="Tłumaczenia" right={
      <select value={lang} onChange={e => setLang(e.target.value)} style={{ maxWidth: 120 }}>
        <option value="pl">pl</option>
        <option value="en">en</option>
      </select>
    }>
      <Alert kind="err">{err}</Alert>
      {keys.length === 0 ? <div className="empty">Brak zdefiniowanych kluczy i18n.</div> : (
        <table>
          <thead><tr><th>Klucz</th><th>Domyślny (pl)</th><th>Wartość ({lang})</th><th /></tr></thead>
          <tbody>
            {keys.map(k => {
              const t = byKey[k.id]
              const value = edits[k.id] ?? t?.value ?? ''
              return (
                <tr key={k.id}>
                  <td className="mono small">{k.namespace}.{k.key}</td>
                  <td className="small">{k.default_text}</td>
                  <td><textarea rows={1} value={value} onChange={e => setEdits(s => ({ ...s, [k.id]: e.target.value }))} /></td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm" disabled={busyKey === k.id} onClick={() => save(k.id)}>Zapisz</button>
                    {cfg.features.ai_translations && (
                      <button className="btn btn-sm btn-ghost" disabled={busyKey === k.id} onClick={() => suggest(k.id)}>AI</button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Powiadomienia (log)
// ---------------------------------------------------------------------------
function NotificationsLogTab() {
  const [items, setItems] = useState([])
  const [err, setErr] = useState('')
  useEffect(() => { api.adminNotifications({ limit: 200 }).then(setItems).catch(e => setErr(e.message)) }, [])
  const { page, setPage, pages, total, slice } = usePager(items, 20)

  return (
    <Card title="Powiadomienia w systemie" desc="Podgląd ostatnich powiadomień wszystkich użytkowników — wyłącznie do wglądu.">
      <Alert kind="err">{err}</Alert>
      {items.length === 0 ? <div className="empty">Brak powiadomień.</div> : (
        <>
          <table>
            <thead><tr><th>Data</th><th>Użytkownik</th><th>Organizacja</th><th>Typ</th><th>Tytuł</th><th>Przeczytane</th></tr></thead>
            <tbody>
              {slice.map(n => (
                <tr key={n.id}>
                  <td className="muted small">{new Date(n.created_at).toLocaleString('pl-PL')}</td>
                  <td className="mono small">{n.user_id || '—'}</td>
                  <td className="mono small">{n.organization_id || '—'}</td>
                  <td><span className="pill">{n.kind}</span></td>
                  <td className="small">{n.title_key}</td>
                  <td className="small">{n.read_at ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pages={pages} setPage={setPage} total={total} />
        </>
      )}
    </Card>
  )
}

export default function Settings() {
  const [tab, setTab] = useState('general')
  return (
    <>
      <div className="topbar">
        <div><h1>Ustawienia</h1><div className="sub">Konfiguracja instancji, integracje, e-maile, tłumaczenia.</div></div>
      </div>
      <div className="content">
        <div className="tabs">
          {TABS.map(t => <div key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</div>)}
        </div>
        {tab === 'general' && <GeneralTab />}
        {tab === 'integrations' && <IntegrationsTab />}
        {tab === 'templates' && <TemplatesTab />}
        {tab === 'translations' && <TranslationsTab />}
        {tab === 'notifications' && <NotificationsLogTab />}
      </div>
    </>
  )
}
